// File: src/components/EventPreview/TicketCard.jsx
import React from 'react';
import { LuReceiptText } from 'react-icons/lu';

function formatMonDotDate(input) {
  if (!input) return '';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const MO = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'];
  const month = MO[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();
  return `${month} ${day}, ${year}`;
}

export default function TicketCard({
  // Behavior
  onView,                  // (ticketOrNull) => void
  ticket = null,           // ticket-like object or null
  cta = 'View',            // (ignored when no ticket; always shows "View")
  className,               // optional wrapper class

  // Minimal props we care about now
  orderNumber,             // string | number (receipt / order id)
  purchasedAt,             // Date | string | number (time of purchase)
}) {
  const t = ticket || {};

  // Include `orderId` in the fallback chain
  const receipt =
    orderNumber ??
    t.orderNumber ??
    t.orderId ??
    t.receiptNumber ??
    t.receiptNo ??
    t.code ??
    t._id ??
    t.id ??
    '';

  // Prefer provided prop; then look for common fields
  const purchaseWhen =
    purchasedAt ??
    t.purchasedAt ??
    t.createdAt ??
    t.paidAt ??
    null;

  const hasTicket = Boolean(receipt || purchaseWhen || ticket);

  // Text
  const headerText = hasTicket ? 'Order receipt' : 'No ticket yet';
  const receiptDisplay = hasTicket && receipt ? `Order #${String(receipt)}` : null;
  const whenDisplay = hasTicket && purchaseWhen ? `Purchased ${formatMonDotDate(purchaseWhen)}` : null;
  const secondaryLine = hasTicket
    ? [receiptDisplay, whenDisplay].filter(Boolean).join(' · ')
    : 'Buy a ticket to see your order details here.';

  const handleClick = () => {
    if (!hasTicket) return;
    if (typeof onView === 'function') return onView(t);
    if (t.url) window.open(t.url, '_blank', 'noopener,noreferrer');
  };

  // Always show "View"; disable (grey out) when no ticket
  const ctaLabel = 'View';

  return (
    <div
      className={className}
      style={{ ...S.banner, ...(hasTicket ? {} : S.bannerGhost) }}
      role="region"
      aria-label={hasTicket ? 'Ticket: order receipt' : 'Ticket: none yet'}
    >
      {/* Left icon tile (restored EXACT old design) */}
      <div style={{ ...S.bannerIcon, ...(hasTicket ? {} : S.bannerIconGhost) }} aria-hidden>
        <div style={S.iconTile}>
          <LuReceiptText style={{ fontSize: 18, color: hasTicket ? '#0B0B0B' : '#9CA3AF' }} />
        </div>
      </div>

      {/* Center content */}
      <div style={S.bannerContent}>
        <div style={S.bannerTextWrapper}>
          <div style={{ ...S.bannerHeader, ...(hasTicket ? {} : S.bannerHeaderGhost) }} title={headerText}>
            {headerText}
          </div>
          <span style={{ ...S.bannerText, ...(hasTicket ? {} : S.bannerTextGhost) }} title={secondaryLine}>
            {secondaryLine}
          </span>
        </div>

        {/* Primary CTA */}
        <button
          type="button"
          disabled={!hasTicket}
          aria-disabled={!hasTicket}
          style={{ ...S.primaryBtn, ...(!hasTicket ? S.primaryBtnDisabled : {}) }}
          onClick={handleClick}
          aria-label={hasTicket ? 'Open ticket' : 'View (disabled)'}
          tabIndex={hasTicket ? 0 : -1}
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}

/* PWA-aligned compact styles */
const S = {
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 8px',
    margin: '8px 0px 8px',
    background: '#F9F9F9',
    backdropFilter: 'blur(8px)',
    borderRadius: 12,
  },
  bannerGhost: {
    background: '#FCFCFC',
    border: '1px dashed #eee',
    color: '#6B7280',
  },

  // ⬇️ Restored exact old design for icon box
  bannerIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    background: '#fff',
    border: '0.5px solid #eee',        // ← back to the original thin light border
  },
  bannerIconGhost: {
    background: '#fff',
    border: '0.5px solid #eee',
  },

  iconTile: {
    width: 26,
    height: 26,
    borderRadius: 8,
    display: 'grid',
    placeItems: 'center',
  },
  bannerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
    marginLeft: 6,
    flexWrap: 'nowrap',
    minWidth: 0,
    gap: 8,
  },
  bannerTextWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: -4,
    flex: 1,
    minWidth: 0,
  },
  bannerHeader: {
    fontSize: 12.5,
    fontWeight: 800,
    color: '#111',
    lineHeight: 1.25,
    marginBottom: 1,
    letterSpacing: 0.1,
    textTransform: 'none',
  },
  bannerHeaderGhost: { color: '#6B7280' },
  bannerText: {
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.3,
    color: '#222',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    marginRight: 20,
  },
  bannerTextGhost: { color: '#6B7280' },

  primaryBtn: {
    background: '#0B0B0B',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 12px',
    fontWeight: 800,
    cursor: 'pointer',
    fontSize: 11,
    letterSpacing: '.04em',
    flexShrink: 0,
  },
  primaryBtnDisabled: {
    opacity: 0.95,
    cursor: 'not-allowed',
    pointerEvents: 'none',
    background: '#ddd',

  },
};
